import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const CHANNEL_OFFSET = 0x10
const CHANNEL_SIZE = 0x10

type T18ProfileSpec = {
  id: string
  vendor: string
  model: string
  magic: string
  fingerprints: readonly (readonly number[])[]
  channels: number
  powerLevels?: readonly ['High', 'Low']
  blockSize?: 8 | 16
  memorySize?: number
  ackBlock?: boolean
  echo?: boolean
  exit?: string
  handshakeAck?: number
  passwordStep?: boolean
  ranges?: readonly (readonly [start: number, end: number])[]
  channelOffset?: number
  channelFormat?: 't18' | 'rb18'
}

export type T18Profile = {
  protocol: 't18'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 0
  powerLevels: readonly ['High', 'Low']
  blockSize: 8 | 16
  memorySize: number
  ranges: readonly (readonly [start: number, end: number])[]
  channelOffset: number
  channelFormat: 't18' | 'rb18'
  magic: readonly number[]
  fingerprints: readonly (readonly number[])[]
  ackBlock: boolean
  echo: boolean
  exitBytes: readonly number[]
  handshakeAck: number
  passwordStep: boolean
}

function ascii(value: string): readonly number[] {
  return Array.from(new TextEncoder().encode(value))
}

function createT18Profile(spec: T18ProfileSpec): T18Profile {
  const memorySize = spec.memorySize ?? 0x03f0
  return {
    protocol: 't18',
    id: spec.id,
    vendor: spec.vendor,
    model: spec.model,
    summary: 'Compact T18-family channel radio with CTCSS and DCS support.',
    tags: ['USB', 'Read + write', `${spec.channels} channels`, 'No channel names'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: spec.channels,
    nameLength: 0,
    powerLevels: spec.powerLevels ?? ['High', 'Low'],
    blockSize: spec.blockSize ?? 8,
    memorySize,
    ranges: spec.ranges ?? [[0, memorySize]],
    channelOffset: spec.channelOffset ?? CHANNEL_OFFSET,
    channelFormat: spec.channelFormat ?? 't18',
    magic: ascii(spec.magic),
    fingerprints: spec.fingerprints,
    ackBlock: spec.ackBlock ?? true,
    echo: spec.echo ?? false,
    exitBytes: ascii(spec.exit ?? 'b'),
    handshakeAck: spec.handshakeAck ?? ACK,
    passwordStep: spec.passwordStep ?? false,
  }
}

const SMP_COUNT = ascii('SMP558\u0000\u0000')
const P3107 = [0x50, 0x33, 0x31, 0x30, 0x37, 0xf7, 0x00, 0x00]
const V06 = [0x06, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00]
const V53 = [0x53, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00]

const STANDARD_T18: readonly T18ProfileSpec[] = [
  { id: 'radtel-t18', vendor: 'Radtel', model: 'T18', magic: '1ROGRAM', fingerprints: [SMP_COUNT], channels: 16 },
  { id: 'retevis-rt20', vendor: 'Retevis', model: 'RT20', magic: '8AOGRAM', fingerprints: [SMP_COUNT], channels: 16 },
  { id: 'retevis-rt22s', vendor: 'Retevis', model: 'RT22S', magic: '9COGRAM', fingerprints: [SMP_COUNT], channels: 22, ackBlock: false },
  { id: 'retevis-rt68', vendor: 'Retevis', model: 'RT68', magic: '83OGRAM', fingerprints: [V06], channels: 16, ackBlock: false, exit: '' },
  { id: 'retevis-rt668', vendor: 'Retevis', model: 'RT668', magic: '83OGRAM', fingerprints: [V06], channels: 16, ackBlock: false, exit: '' },
  { id: 'retevis-rb17', vendor: 'Retevis', model: 'RB17', magic: 'A5OGRAM', fingerprints: [V53], channels: 16, ackBlock: false },
  { id: 'retevis-rb617', vendor: 'Retevis', model: 'RB617', magic: 'A5OGRAM', fingerprints: [V53], channels: 16, ackBlock: false },
  { id: 'retevis-rb17v', vendor: 'Retevis', model: 'RB17V', magic: 'A5OGRAM', fingerprints: [V53], channels: 5, ackBlock: false },
  { id: 'retevis-rb18', vendor: 'Retevis', model: 'RB18', magic: 'PROGRAL', fingerprints: [P3107], channels: 22, memorySize: 0x0660, blockSize: 16, channelOffset: 0, channelFormat: 'rb18', exit: 'E' },
  { id: 'retevis-rb618', vendor: 'Retevis', model: 'RB618', magic: 'PROGRAL', fingerprints: [P3107], channels: 16, memorySize: 0x0660, blockSize: 16, channelOffset: 0, channelFormat: 'rb18', exit: 'E' },
  { id: 'retevis-rb85', vendor: 'Retevis', model: 'RB85', magic: 'H19GRAM', fingerprints: [SMP_COUNT], channels: 16, ackBlock: false },
  { id: 'retevis-rb75', vendor: 'Retevis', model: 'RB75', magic: 'KVOGRAM', fingerprints: [SMP_COUNT], channels: 30, ackBlock: false },
  { id: 'btech-frs-b1', vendor: 'BTECH', model: 'FRS-B1', magic: 'PROGRAM', fingerprints: [P3107], channels: 22 },
  { id: 'retevis-rb19', vendor: 'Retevis', model: 'RB19', magic: '9COGRAM', fingerprints: [SMP_COUNT], channels: 22, ackBlock: false },
  { id: 'retevis-rb19p', vendor: 'Retevis', model: 'RB19P', magic: '70OGRAM', fingerprints: [SMP_COUNT], channels: 30, ackBlock: false },
  { id: 'retevis-rb619', vendor: 'Retevis', model: 'RB619', magic: '9COGRAM', fingerprints: [SMP_COUNT], channels: 16, ackBlock: false },
  { id: 'retevis-rt47', vendor: 'Retevis', model: 'RT47', magic: '47OGRAM', fingerprints: [V06], channels: 16, ackBlock: false, echo: true },
  { id: 'retevis-rt47v', vendor: 'Retevis', model: 'RT47V', magic: '47OGRAM', fingerprints: [V06], channels: 5, ackBlock: false, echo: true },
  { id: 'retevis-rt647', vendor: 'Retevis', model: 'RT647', magic: '47OGRAM', fingerprints: [V06], channels: 16, ackBlock: false, echo: true, handshakeAck: 0xf0 },
  { id: 'baofeng-bf-v8a', vendor: 'Baofeng', model: 'BF-V8A', magic: 'PROGRAM', fingerprints: [P3107], channels: 16 },
  { id: 'retevis-rb29', vendor: 'Retevis', model: 'RB29', magic: 'S19GRAM', fingerprints: [SMP_COUNT], channels: 16, ackBlock: false },
  { id: 'retevis-rb629', vendor: 'Retevis', model: 'RB629', magic: 'S19GRAM', fingerprints: [SMP_COUNT], channels: 16, ackBlock: false },
  { id: 'retevis-rt15', vendor: 'Retevis', model: 'RT15', magic: 'KAOGRAM', fingerprints: [V06, [0x06, 0x03, 0xe8, 0x08, 0xff, 0xff, 0xff, 0xff], [0x06, 0x4d, 0x50, 0x35, 0x35, 0x38, 0xff, 0xff]], channels: 16, ackBlock: false },
  { id: 'retevis-rt18', vendor: 'Retevis', model: 'RT18', magic: 'JSOGRAP', fingerprints: [[0x06]], channels: 16, ackBlock: false, passwordStep: true },
  { id: 'baofeng-bf-t20frs', vendor: 'Baofeng', model: 'BF-T20FRS', magic: 'PROGRAM', fingerprints: [P3107], channels: 22, memorySize: 0x03f0, blockSize: 16, ranges: [[0x0000, 0x0160], [0x02b0, 0x02d0]], channelOffset: 0 },
  { id: 'retevis-rb87', vendor: 'Retevis', model: 'RB87', magic: 'C8OGRAN', fingerprints: [ascii('SMP558')], channels: 30, ackBlock: false, exit: '' },
  { id: 'retevis-h777h-frs', vendor: 'Retevis', model: 'H777H_FRS', magic: 'C701RAD', fingerprints: [SMP_COUNT], channels: 16, ackBlock: false },
  { id: 'retevis-h777h-pmr', vendor: 'Retevis', model: 'H777H_PMR', magic: 'C701RAD', fingerprints: [SMP_COUNT], channels: 16, ackBlock: false },
]

export const T18_PROFILES: readonly T18Profile[] = STANDARD_T18.map(createT18Profile)

function decodeToneValue(raw: Uint8Array): number | null {
  if (raw.every((value) => value === 0xff)) return null
  let value = 0
  for (let index = raw.length - 1; index >= 0; index -= 1) {
    const high = raw[index] >> 4
    const low = raw[index] & 0x0f
    value = value * 100 + high * 10 + low
  }
  return value
}

function bcdByte(value: number): number {
  return ((Math.floor(value / 10) << 4) | (value % 10)) & 0xff
}

function decodeT18Tone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  const value = decodeToneValue(raw)
  if (value === null || value === 16665) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  if (value >= 12000) {
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: value - 12000,
      dtcsPolarity: 'R',
    }
  }
  if (value >= 8000) {
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: value - 8000,
      dtcsPolarity: 'N',
    }
  }
  return {
    toneMode: 'ctcss',
    ctcss: value / 10,
    dtcs: null,
    dtcsPolarity: 'N',
  }
}

function encodeT18Tone(channel: WebChannel): readonly [number, number] {
  if (channel.toneMode === 'none') return [0xff, 0xff]
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const value = Math.round(channel.ctcss * 10)
    return [bcdByte(value % 100), bcdByte(Math.floor(value / 100) % 100)]
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    return [
      bcdByte(channel.dtcs % 100),
      bcdByte(Math.floor(channel.dtcs / 100) % 100) |
        (channel.dtcsPolarity === 'R' ? 0xc0 : 0x80),
    ]
  }
  return [0xff, 0xff]
}

function decodeRb18Bcd(raw: Uint8Array): number | null {
  if (raw.every((value) => value === 0xff)) return null
  let value = 0
  for (let index = raw.length - 1; index >= 0; index -= 1) {
    const byte = raw[index]
    const high = byte >> 4
    const low = byte & 0x0f
    if (high > 9 || low > 9) return null
    value = value * 100 + high * 10 + low
  }
  return value
}

function encodeRb18Bcd(value: number): [number, number] {
  const result = new Uint8Array(2)
  let remaining = value
  for (let index = 0; index < result.length; index += 1) {
    const part = remaining % 100
    result[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    remaining = Math.floor(remaining / 100)
  }
  return [result[0], result[1]]
}

function decodeRb18Tone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  if (raw.every((value) => value === 0xff)) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  const isDtcs = (raw[1] & 0x80) !== 0
  const polarity = (raw[1] & 0x40) !== 0 ? 'R' : 'N'
  const masked = raw.slice()
  masked[1] &= 0x3f
  const value = decodeRb18Bcd(masked)
  if (!value) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  return isDtcs
    ? { toneMode: 'dtcs', ctcss: null, dtcs: value, dtcsPolarity: polarity }
    : { toneMode: 'ctcss', ctcss: value / 10, dtcs: null, dtcsPolarity: 'N' }
}

function encodeRb18Tone(channel: WebChannel): readonly [number, number] {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    return encodeRb18Bcd(Math.round(channel.ctcss * 10))
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const raw = encodeRb18Bcd(channel.dtcs)
    raw[1] |= 0x80
    if (channel.dtcsPolarity === 'R') raw[1] |= 0x40
    return raw
  }
  return [0xff, 0xff]
}

function parseRb18Channel(
  image: Uint8Array,
  profile: T18Profile,
  number: number,
): WebChannel {
  const offset = profile.channelOffset + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  const rx = raw.byteLength === CHANNEL_SIZE ? decodeLbcd(raw.slice(0, 4)) : null
  if (!rx) {
    return {
      number,
      empty: true,
      name: '',
      rxFrequency: null,
      txFrequency: null,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
      mode: 'NFM',
      power: 'Low',
      skip: false,
    }
  }
  return {
    number,
    empty: false,
    name: '',
    rxFrequency: rx,
    txFrequency: decodeLbcd(raw.slice(4, 8)),
    ...decodeRb18Tone(raw.slice(8, 10)),
    mode: (raw[12] & 0x04) !== 0 ? 'NFM' : 'FM',
    power: (raw[12] & 0x08) !== 0 ? 'High' : 'Low',
    skip: (raw[12] & 0x10) !== 0,
  }
}

function applyRb18Channel(
  image: Uint8Array,
  profile: T18Profile,
  channel: WebChannel,
): void {
  const offset = profile.channelOffset + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  const preserved = raw[12] & 0xc3
  raw.fill(0, 0, 12)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  const tone = encodeRb18Tone(channel)
  raw.set(tone, 8)
  raw.set(tone, 10)
  raw[12] =
    preserved |
    (channel.skip ? 0x10 : 0) |
    (channel.power === 'High' ? 0x08 : 0) |
    (channel.mode === 'NFM' ? 0x04 : 0)
}

export function parseT18Channel(
  image: Uint8Array,
  profile: T18Profile,
  number: number,
): WebChannel {
  if (profile.channelFormat === 'rb18') {
    return parseRb18Channel(image, profile, number)
  }
  const offset = profile.channelOffset + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  const rx = raw.byteLength === CHANNEL_SIZE ? decodeLbcd(raw.slice(0, 4)) : null
  if (!rx) {
    return {
      number,
      empty: true,
      name: '',
      rxFrequency: null,
      txFrequency: null,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
      mode: 'NFM',
      power: 'Low',
      skip: false,
    }
  }
  return {
    number,
    empty: false,
    name: '',
    rxFrequency: rx,
    txFrequency: decodeLbcd(raw.slice(4, 8)),
    ...decodeT18Tone(raw.slice(8, 10)),
    mode: (raw[12] & 0x04) !== 0 ? 'NFM' : 'FM',
    power: (raw[12] & 0x08) !== 0 ? 'High' : 'Low',
    skip: (raw[12] & 0x10) !== 0,
  }
}

export function parseT18Channels(
  image: Uint8Array,
  profile: T18Profile,
): WebChannel[] {
  return Array.from({ length: profile.channelMaximum }, (_, index) =>
    parseT18Channel(image, profile, index + 1),
  )
}

export function applyT18Channel(
  image: Uint8Array,
  profile: T18Profile,
  channel: WebChannel,
): void {
  if (profile.channelFormat === 'rb18') {
    applyRb18Channel(image, profile, channel)
    return
  }
  const offset = profile.channelOffset + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')

  const wasEmpty = decodeLbcd(raw.slice(0, 4)) === null
  const preserved = wasEmpty ? 0xf9 : raw[12] & 0xe3
  if (wasEmpty) {
    raw.fill(0)
    raw[12] = 0xf9
    raw.fill(0xff, 13)
  }
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  const tone = encodeT18Tone(channel)
  raw.set(tone, 8)
  raw.set(tone, 10)
  raw[12] =
    preserved |
    (channel.skip ? 0x10 : 0) |
    (channel.power === 'High' ? 0x08 : 0) |
    (channel.mode === 'NFM' ? 0x04 : 0)
}

async function chew(transport: RadioTransport, size: number): Promise<void> {
  if (size > 0) await transport.readExact(size, 1200)
}

async function enterProgrammingMode(
  profile: T18Profile,
  transport: RadioTransport,
): Promise<Uint8Array> {
  await transport.discardInput()
  const magic = Uint8Array.from([0x02, ...profile.magic])
  await transport.write(magic)
  if (profile.echo) await chew(transport, magic.byteLength)
  if ((await transport.readExact(1, 1500))[0] !== ACK) {
    throw new Error('The radio refused to enter T18 programming mode.')
  }

  await transport.write(Uint8Array.of(0x02))
  if (profile.echo) await chew(transport, 1)
  const ident = await transport.readExact(8, 1500)
  if (
    !profile.fingerprints.some((fingerprint) =>
      fingerprint.every((value, index) => ident[index] === value),
    )
  ) {
    throw new Error(
      `The connected radio does not report the ${profile.vendor} ${profile.model} T18 identity.`,
    )
  }

  await transport.write(Uint8Array.of(ACK))
  if (profile.echo) await chew(transport, 1)
  if ((await transport.readExact(1, 1500))[0] !== profile.handshakeAck) {
    throw new Error('The radio refused the T18 programming session.')
  }
  if (profile.passwordStep) {
    await transport.write(Uint8Array.of(0x05))
    if (profile.echo) await chew(transport, 1)
    await transport.readExact(6, 1500)
    await transport.write(Uint8Array.of(ACK))
    if (profile.echo) await chew(transport, 1)
    if ((await transport.readExact(1, 1500))[0] !== ACK) {
      throw new Error('The radio refused the T18 password step.')
    }
  }
  return ident
}

async function exitProgrammingMode(
  profile: T18Profile,
  transport: RadioTransport,
): Promise<void> {
  if (!profile.exitBytes.length) return
  const command = Uint8Array.from(profile.exitBytes)
  await transport.write(command)
  if (profile.echo) await chew(transport, command.byteLength)
}

async function readBlock(
  profile: T18Profile,
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  const size = profile.blockSize
  const command = Uint8Array.of(
    0x52,
    (address >> 8) & 0xff,
    address & 0xff,
    size,
  )
  await transport.write(command)
  if (profile.echo) await chew(transport, command.byteLength)
  const response = await transport.readExact(size + 4, 2000)
  if (
    response[0] !== 0x57 ||
    response[1] !== command[1] ||
    response[2] !== command[2] ||
    response[3] !== size
  ) {
    throw new Error(`Unexpected T18 read reply at 0x${address.toString(16)}.`)
  }
  if (profile.ackBlock) {
    await transport.write(Uint8Array.of(ACK))
    if (profile.echo) await chew(transport, 1)
    if ((await transport.readExact(1, 1200))[0] !== ACK) {
      throw new Error(`T18 did not acknowledge read block 0x${address.toString(16)}.`)
    }
  }
  return response.slice(4)
}

async function writeBlock(
  profile: T18Profile,
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const command = Uint8Array.of(
    0x57,
    (address >> 8) & 0xff,
    address & 0xff,
    data.byteLength,
  )
  const frame = new Uint8Array(4 + data.byteLength)
  frame.set(command)
  frame.set(data, 4)
  await transport.write(frame)
  if (profile.echo) await chew(transport, frame.byteLength)
  if ((await transport.readExact(1, 1500))[0] !== ACK) {
    throw new Error(`T18 rejected write block 0x${address.toString(16)}.`)
  }
}

export async function readT18RadioWithTransport(
  profile: T18Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const ident = await enterProgrammingMode(profile, transport)
  const image = new Uint8Array(profile.memorySize)
  const total = Math.ceil(profile.memorySize / profile.blockSize)
  for (let address = 0; address < profile.memorySize; address += profile.blockSize) {
    image.set(
      await readBlock(profile, transport, address),
      address,
    )
    onProgress?.({
      current: address / profile.blockSize + 1,
      total,
      label: 'Reading T18 memory',
    })
  }
  await exitProgrammingMode(profile, transport)
  return {
    image,
    reportedModel:
      new TextDecoder('ascii').decode(ident).replace(/[\x00-\x1f\x7f-\xff]/g, '').trim() ||
      profile.model,
  }
}

export async function writeT18RadioWithTransport(
  profile: T18Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  await enterProgrammingMode(profile, transport)
  const total = profile.ranges.reduce(
    (count, [start, end]) => count + Math.ceil((end - start) / profile.blockSize),
    0,
  )
  let completed = 0
  for (const [start, end] of profile.ranges) {
    for (let address = start; address < end; address += profile.blockSize) {
      await writeBlock(
        profile,
        transport,
        address,
        image.slice(address, address + profile.blockSize),
      )
      completed += 1
      onProgress?.({ current: completed, total, label: 'Writing T18 memory' })
    }
  }
  await exitProgrammingMode(profile, transport)
}
